import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { appsAPI } from '../api/appsAPI';
import UserProfileDropdown from '../../../components/UserProfileDropdown/UserProfileDropdown';
import { Logo_black_iconOnly } from '../../../components/Logo/Logo';
import AppBarBottom from './AppBarBottom';
import ChatSidePanel from './ChatSidePanel';
import ChatAgentPanel from './ChatAgentPanel';
import { useAppStoreHome } from '../context/AppStoreHomeContext';
import SkeletonLoader from './SkeletonLoader';
import { isHiddenTaskListApp } from '../utils/appDisplayUtils';
import './AppStoreView.css';
import './LandingAppStore.css';

const promoEvents = [
  {
    key: 'uai',
    badge: 'Ends on 07/31',
    headline: 'Plan your studies, organize tasks, and get instant feedback with uAI',
    description: 'Unleash your potential with our advanced AI tutor, personalized study planner, and instant case feedback.',
    appTitle: 'uAI Study Assistant',
    appSubtext: 'Ivori • AI Tools',
    defaultIcon: '/uai-icon.png',
    defaultBanner: '/api/apps/media/tutorials/uai/uai-1920x1080.png'
  },
  {
    key: 'code_lab',
    badge: 'Ends on 07/15',
    headline: 'Learn dynamic coding and run code in real-time',
    description: 'Practice Python, JS and SQL directly in your browser with interactive sandboxed code playgrounds.',
    appTitle: 'Code Lab',
    appSubtext: 'Ivori • Coding',
    defaultIcon: '/logo192.png',
    defaultBanner: '/api/apps/media/tutorials/code_lab/code_lab-1920x1080.png'
  },
  {
    key: 'computer_science_interview',
    badge: 'Ends on 06/30',
    headline: 'Prep for top tech companies with mock programming cases',
    description: 'Crack the algorithmic and design interviews using our step-by-step case interactive simulator.',
    appTitle: 'Computer Science Interview',
    appSubtext: 'Ivori • Computer Science',
    defaultIcon: '/logo192.png',
    defaultBanner: '/api/apps/media/tutorials/computer_science_interview/computer_science_interview-1920x1080.png'
  }
];

const AppStoreView = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState(null); // null = show all subjects
  
  const [chatOpen, setChatOpen] = useState(false);
  const [chatAppOpen, setChatAppOpen] = useState(false);
  const [chatLayerMounted, setChatLayerMounted] = useState(false);
  const [chatLayerActive, setChatLayerActive] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const userName = user?.name || 'Student';
  const userEmail = user?.email || 'student@pathbuddy.com';

  const {
    allBubbles,
    deleteBubble,
    restoreBubble,
  } = useAppStoreHome();

  // Scroll controls for App Rows
  const caseRowRef = useRef(null);
  const eventsRowRef = useRef(null);
  const tutorialRowRef = useRef(null);
  const productivityRowRef = useRef(null);
  const libraryRowRef = useRef(null);
  const subjectRowRef = useRef(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [canScrollEventsLeft, setCanScrollEventsLeft] = useState(false);
  const [canScrollEventsRight, setCanScrollEventsRight] = useState(true);
  const [canScrollTutorialLeft, setCanScrollTutorialLeft] = useState(false);
  const [canScrollTutorialRight, setCanScrollTutorialRight] = useState(true);
  const [canScrollProductivityLeft, setCanScrollProductivityLeft] = useState(false);
  const [canScrollProductivityRight, setCanScrollProductivityRight] = useState(true);
  const [canScrollLibraryLeft, setCanScrollLibraryLeft] = useState(false);
  const [canScrollLibraryRight, setCanScrollLibraryRight] = useState(true);
  const [canScrollSubjectLeft, setCanScrollSubjectLeft] = useState(false);
  const [canScrollSubjectRight, setCanScrollSubjectRight] = useState(true);

  const updateScrollButtons = () => {
    const el = caseRowRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  const updateEventsScrollButtons = () => {
    const el = eventsRowRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollEventsLeft(scrollLeft > 10);
      setCanScrollEventsRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  const updateTutorialScrollButtons = () => {
    const el = tutorialRowRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollTutorialLeft(scrollLeft > 10);
      setCanScrollTutorialRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  const updateProductivityScrollButtons = () => {
    const el = productivityRowRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollProductivityLeft(scrollLeft > 10);
      setCanScrollProductivityRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  const updateLibraryScrollButtons = () => {
    const el = libraryRowRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollLibraryLeft(scrollLeft > 10);
      setCanScrollLibraryRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  const updateSubjectScrollButtons = () => {
    const el = subjectRowRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollSubjectLeft(scrollLeft > 10);
      setCanScrollSubjectRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  const handleScrollClick = (direction) => {
    const el = caseRowRef.current;
    if (el) {
      const cardWidth = 204;
      el.scrollBy({ left: direction === 'left' ? -cardWidth : cardWidth, behavior: 'smooth' });
    }
  };

  const handleEventsScrollClick = (direction) => {
    const el = eventsRowRef.current;
    if (el) {
      const cardWidth = 384;
      el.scrollBy({ left: direction === 'left' ? -cardWidth : cardWidth, behavior: 'smooth' });
    }
  };

  const handleTutorialScrollClick = (direction) => {
    const el = tutorialRowRef.current;
    if (el) {
      const cardWidth = 204;
      el.scrollBy({ left: direction === 'left' ? -cardWidth : cardWidth, behavior: 'smooth' });
    }
  };

  const handleProductivityScrollClick = (direction) => {
    const el = productivityRowRef.current;
    if (el) {
      const cardWidth = 204;
      el.scrollBy({ left: direction === 'left' ? -cardWidth : cardWidth, behavior: 'smooth' });
    }
  };

  const handleLibraryScrollClick = (direction) => {
    const el = libraryRowRef.current;
    if (el) {
      const cardWidth = 204;
      el.scrollBy({ left: direction === 'left' ? -cardWidth : cardWidth, behavior: 'smooth' });
    }
  };

  const handleSubjectScrollClick = (direction) => {
    const el = subjectRowRef.current;
    if (el) {
      el.scrollBy({ left: direction === 'left' ? -350 : 350, behavior: 'smooth' });
    }
  };

  // Fetch all apps on mount
  useEffect(() => {
    const fetchApps = async () => {
      try {
        setLoading(true);
        const data = await appsAPI.getApps();
        let appList = Array.isArray(data) ? data : [];
        const tmuaApps = appList.filter(a => (a.exam_name || '').toLowerCase() === 'tmua' || (a.title || '').toLowerCase().includes('tmua'));
        if (tmuaApps.length > 1) {
          const primaryTmua = tmuaApps.find(a => !(a.subject || '').toLowerCase().includes('computer science')) || tmuaApps[0];
          appList = appList.filter(a => {
            const isTmua = (a.exam_name || '').toLowerCase() === 'tmua' || (a.title || '').toLowerCase().includes('tmua');
            if (isTmua && a.id !== primaryTmua.id) return false;
            return true;
          });
        }
        setApps(appList);
      } catch (error) {
        console.error('Error fetching apps:', error);
        setApps([]);
      } finally {
        setLoading(false);
      }
    };
    fetchApps();
  }, []);

  // Update button visibility when subjects filter changes
  useEffect(() => {
    const el = caseRowRef.current;
    if (el) el.scrollLeft = 0;
    const elEvents = eventsRowRef.current;
    if (elEvents) elEvents.scrollLeft = 0;
    const elTutorial = tutorialRowRef.current;
    if (elTutorial) elTutorial.scrollLeft = 0;
    const elProductivity = productivityRowRef.current;
    if (elProductivity) elProductivity.scrollLeft = 0;
    const elLibrary = libraryRowRef.current;
    if (elLibrary) elLibrary.scrollLeft = 0;

    const timer = setTimeout(() => {
      updateScrollButtons();
      updateEventsScrollButtons();
      updateTutorialScrollButtons();
      updateProductivityScrollButtons();
      updateLibraryScrollButtons();
      updateSubjectScrollButtons();
    }, 100);
    return () => clearTimeout(timer);
  }, [selectedSubject, apps]);

  useEffect(() => {
    window.addEventListener('resize', updateScrollButtons);
    window.addEventListener('resize', updateEventsScrollButtons);
    window.addEventListener('resize', updateTutorialScrollButtons);
    window.addEventListener('resize', updateProductivityScrollButtons);
    window.addEventListener('resize', updateLibraryScrollButtons);
    window.addEventListener('resize', updateSubjectScrollButtons);
    return () => {
      window.removeEventListener('resize', updateScrollButtons);
      window.removeEventListener('resize', updateEventsScrollButtons);
      window.removeEventListener('resize', updateTutorialScrollButtons);
      window.removeEventListener('resize', updateProductivityScrollButtons);
      window.removeEventListener('resize', updateLibraryScrollButtons);
      window.removeEventListener('resize', updateSubjectScrollButtons);
    };
  }, []);

  const handleAppClick = (appId) => {
    navigate(`/apps-store/${appId}`);
  };

  // Categorize apps
  const tutorialOnlyApps = apps.filter(
    (app) => app.app_type === 'tutorial'
  );
  const productivityAndUtilityApps = apps.filter(
    (app) =>
      (app.app_type === 'productivity' || app.app_type === 'utility') &&
      (app.exam_name || '').toLowerCase() !== 'library' &&
      !isHiddenTaskListApp(app)
  );

  // Filters by selected subject
  const filteredTutorialOnlyApps = (selectedSubject
    ? tutorialOnlyApps.filter((app) => (app.subject || '').toLowerCase() === selectedSubject.toLowerCase() || (app.title || '').toLowerCase() === 'caius explore')
    : tutorialOnlyApps
  ).sort((a, b) => {
    const aIsCaius = (a.exam_name || '').toLowerCase() === 'caius_explore' || (a.title || '').toLowerCase() === 'caius explore';
    const bIsCaius = (b.exam_name || '').toLowerCase() === 'caius_explore' || (b.title || '').toLowerCase() === 'caius explore';
    if (aIsCaius && !bIsCaius) return -1;
    if (!aIsCaius && bIsCaius) return 1;
    return 0;
  });

  const experienceApps = apps.filter(
    (app) => app.app_type === 'experience' || app.app_type === 'case_study'
  );
  const filteredExperienceApps = selectedSubject
    ? experienceApps.filter((app) => (app.subject || '').toLowerCase() === selectedSubject.toLowerCase())
    : experienceApps;

  const filteredProductivityAndUtilityApps = selectedSubject
    ? productivityAndUtilityApps.filter((app) => (app.subject || '').toLowerCase() === selectedSubject.toLowerCase() || !app.subject)
    : productivityAndUtilityApps;

  const formatDisplayTitle = (title) => {
    if (!title) return '';
    const lower = title.trim().toLowerCase();
    if (lower === 'tmua') return 'TMUA';
    if (lower === 'lnat') return 'LNAT';
    if (lower === 'ucat') return 'UCAT';
    if (lower === 'mat') return 'MAT';
    return title;
  };

  const isAppComingSoon = (appItem) => {
    if (!appItem) return false;
    if (appItem.is_coming_soon || appItem.coming_soon || appItem.status === 'coming_soon') return true;
    const titleLower = (appItem.title || '').toLowerCase();
    const examLower = (appItem.exam_name || '').toLowerCase();
    const idLower = (appItem.id || '').toLowerCase();
    return (
      titleLower.includes('task list') ||
      examLower.includes('task_list') ||
      idLower.includes('task-list') ||
      idLower.includes('task_list') ||
      titleLower.includes('flow forge') ||
      titleLower.includes('flowforge') ||
      examLower.includes('flow_forge') ||
      idLower.includes('flow_forge') ||
      idLower.includes('flow-forge') ||
      idLower.includes('flowforge') ||
      titleLower.includes('code lab') ||
      titleLower.includes('codelab') ||
      titleLower.includes('python code') ||
      idLower.includes('code-lab') ||
      idLower.includes('code_lab') ||
      idLower.includes('codelab') ||
      idLower.includes('spec-cs-python') ||
      titleLower.includes('coming soon')
    );
  };

  const activeSubjectsList = [
    'Biology',
    'Computer Science',
    'Engineering',
    'Geography',
    'Investment Banking',
    'Law',
    'Management Consulting',
    'Mathematics',
    'Medicine'
  ];

  // uAI Dock & Transition Controls
  const CHAT_TRANSITION_MS = 450;
  const chatTransitionTimerRef = useRef(null);

  const clearChatTransitionTimer = useCallback(() => {
    if (chatTransitionTimerRef.current) {
      clearTimeout(chatTransitionTimerRef.current);
      chatTransitionTimerRef.current = null;
    }
  }, []);

  const openChatApp = useCallback(() => {
    clearChatTransitionTimer();
    setChatAppOpen(true);
    setChatLayerMounted(true);
    setChatLayerActive(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setChatLayerActive(true));
    });
    const params = new URLSearchParams(window.location.search);
    if (params.get('view') !== 'chat') {
      params.set('view', 'chat');
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [clearChatTransitionTimer, navigate, location.pathname]);

  const closeChatApp = useCallback(() => {
    clearChatTransitionTimer();
    setChatLayerActive(false);
    chatTransitionTimerRef.current = setTimeout(() => {
      setChatAppOpen(false);
      setChatLayerMounted(false);
      chatTransitionTimerRef.current = null;
    }, CHAT_TRANSITION_MS);
    const params = new URLSearchParams(window.location.search);
    if (params.get('view') === 'chat') {
      params.delete('view');
      const searchStr = params.toString();
      navigate(searchStr ? `${location.pathname}?${searchStr}` : location.pathname, { replace: true });
    }
  }, [CHAT_TRANSITION_MS, clearChatTransitionTimer, navigate, location.pathname]);

  const toggleChatApp = useCallback(() => {
    if (chatAppOpen) closeChatApp();
    else openChatApp();
  }, [chatAppOpen, closeChatApp, openChatApp]);

  useEffect(() => () => clearChatTransitionTimer(), [clearChatTransitionTimer]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasChatParam = params.get('view') === 'chat';
    if (hasChatParam) {
      navigate('/chat', { replace: true });
    }
  }, [location.search, navigate]);



  return (
    <div className={`landing-app-store app-store-view netflix-style logged-in-store${chatAppOpen ? ' app-store-chat-active' : ''}`}>
      {!chatAppOpen && (
        <header style={{
          padding: '32px 32px 16px 32px',
          boxSizing: 'border-box',
          margin: 0
        }}>
          <h1 style={{
            fontFamily: 'var(--iv-font-head)',
            fontSize: '28px',
            fontWeight: 700,
            color: 'var(--iv-accent)',
            margin: 0,
            lineHeight: 1.2,
            textShadow: 'none'
          }}>
            Academic Tool & Resource Store
          </h1>
          <p style={{
            fontSize: '15px',
            color: 'var(--iv-muted)',
            margin: '6px 0 0 0',
            textAlign: 'left',
            lineHeight: 1.4,
            fontWeight: 400
          }}>
            Explore study planners, mock case simulators, admissions prep exams, and interactive AI assets.
          </p>
        </header>
      )}

      <main 
        className={`landing-app-store-main app-store-main${chatAppOpen ? ' app-store-whiteboard-pushed-back' : ''}`}
        style={{ paddingBottom: '90px', background: 'var(--iv-bg)' }}
        aria-hidden={chatAppOpen}
      >
        {/* ================= HERO ================= */}
        <section className="hero" id="top" style={{ padding: '0 32px 16px 32px', minHeight: 'auto', background: 'transparent' }}>
          <div className="hero-grid-bg" aria-hidden="true" />
        </section>

        {/* Subject filter pills (commented out per user request) */}
        {/*
        <section id="landing-subject-navigation" className="landing-subject-navigation" style={{ background: 'transparent', padding: '10px 48px', margin: '0 auto', maxWidth: '1440px' }} aria-label="Filter by subject">
          <div className="case-apps-row-wrapper" style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
            <button
              type="button"
              className="case-scroll-btn case-scroll-btn-left"
              onClick={() => handleSubjectScrollClick('left')}
              disabled={!canScrollSubjectLeft}
              aria-label="Scroll subjects left"
              style={{ top: '50%', transform: 'translateY(-50%)', left: '-36px', zIndex: 10 }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <div 
              className="subject-nav-container subject-nav-desktop"
              ref={subjectRowRef}
              onScroll={updateSubjectScrollButtons}
              style={{ width: '100%', overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', display: 'flex', gap: '12px', padding: '4px 0' }}
            >
              <button
                type="button"
                className={`subject-nav-button ${selectedSubject === null ? 'active' : ''}`}
                onClick={() => setSelectedSubject(null)}
              >
                All
              </button>
              {activeSubjectsList.map((subject) => (
                <button
                  key={subject}
                  type="button"
                  className={`subject-nav-button ${selectedSubject === subject ? 'active' : ''}`}
                  onClick={() => setSelectedSubject(subject)}
                >
                  {subject}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="case-scroll-btn case-scroll-btn-right"
              onClick={() => handleSubjectScrollClick('right')}
              disabled={!canScrollSubjectRight}
              aria-label="Scroll subjects right"
              style={{ top: '50%', transform: 'translateY(-50%)', right: '-36px', zIndex: 10 }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </section>
        */}

        <div className="app-store-content">
          
          {/* ================= INTERACTIVE CASE STUDIES ================= */}
          {filteredExperienceApps.length > 0 && (
            <div className="column-box productivity-box case-apps-row-box" style={{ margin: '20px 0' }}>
              <h2 className="column-box-title">Interactive Case Studies</h2>
              <div className="case-apps-row-wrapper">
                <button
                  type="button"
                  className="case-scroll-btn case-scroll-btn-left"
                  onClick={() => handleScrollClick('left')}
                  disabled={!canScrollLeft}
                  aria-label="Scroll left"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <div 
                  className="case-apps-row" 
                  id="case-apps-row"
                  ref={caseRowRef}
                  onScroll={updateScrollButtons}
                >
                  {filteredExperienceApps.map((app) => {
                    const imageUrl = app.images?.medium || app.images?.large || app.images?.small || '/logo192.png';
                    return (
                      <div
                        key={app.id}
                        role="button"
                        tabIndex={0}
                        className="column-box-item"
                        onClick={() => handleAppClick(app.id)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAppClick(app.id)}
                      >
                        <img 
                          src={imageUrl} 
                          alt={app.title} 
                          className="column-box-icon" 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/logo192.png';
                          }}
                        />
                        <div className="column-box-info">
                          <h3 className="column-box-item-title">{formatDisplayTitle(app.title)}</h3>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="case-scroll-btn case-scroll-btn-right"
                  onClick={() => handleScrollClick('right')}
                  disabled={!canScrollRight}
                  aria-label="Scroll right"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* ================= TUTORIAL APPS (ADMISSIONS & REASONING) ================= */}
          <div className="column-box productivity-box case-apps-row-box" style={{ margin: '20px 0' }}>
            <h2 className="column-box-title">Admissions & Reasoning Tests</h2>
            <div className="case-apps-row-wrapper">
              <button
                type="button"
                className="case-scroll-btn case-scroll-btn-left"
                onClick={() => handleTutorialScrollClick('left')}
                disabled={!canScrollTutorialLeft}
                aria-label="Scroll left"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div 
                className="case-apps-row" 
                id="tutorial-apps-row"
                ref={tutorialRowRef}
                onScroll={updateTutorialScrollButtons}
              >
                {loading ? (
                  <SkeletonLoader type="squircle-cards" count={4} style={{ padding: 0 }} />
                ) : filteredTutorialOnlyApps.length === 0 ? (
                  <div className="app-store-empty-row">No reasoning or admissions prep apps found matching subject.</div>
                ) : (
                  filteredTutorialOnlyApps.map((app) => {
                    const imageUrl = app.images?.medium || app.images?.large || app.images?.small || '/logo192.png';
                    const rating = app.rating !== undefined ? app.rating : 4.5;
                    return (
                      <div
                        key={app.id}
                        role="button"
                        tabIndex={0}
                        className="column-box-item"
                        onClick={() => handleAppClick(app.id)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAppClick(app.id)}
                      >
                        <img 
                          src={imageUrl} 
                          alt={app.title} 
                          className="column-box-icon" 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/logo192.png';
                          }}
                        />
                        <div className="column-box-info">
                          <h3 className="column-box-item-title">{formatDisplayTitle(app.title)}</h3>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <button
                type="button"
                className="case-scroll-btn case-scroll-btn-right"
                onClick={() => handleTutorialScrollClick('right')}
                disabled={!canScrollTutorialRight}
                aria-label="Scroll right"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>

          {/* ================= PRODUCTIVITY & UTILITIES ================= */}
          <div className="column-box productivity-box case-apps-row-box" style={{ margin: '20px 0' }}>
            <h2 className="column-box-title">Productivity & Utilities</h2>
            <div className="case-apps-row-wrapper">
              <button
                type="button"
                className="case-scroll-btn case-scroll-btn-left"
                onClick={() => handleProductivityScrollClick('left')}
                disabled={!canScrollProductivityLeft}
                aria-label="Scroll left"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div 
                className="case-apps-row" 
                id="productivity-apps-row"
                ref={productivityRowRef}
                onScroll={updateProductivityScrollButtons}
              >
                {loading ? (
                  <SkeletonLoader type="squircle-cards" count={4} style={{ padding: 0 }} />
                ) : filteredProductivityAndUtilityApps.length === 0 ? (
                  <div className="app-store-empty-row">No productivity apps found matching subject.</div>
                ) : (
                  filteredProductivityAndUtilityApps.map((app) => {
                    const imageUrl = app.images?.medium || app.images?.large || app.images?.small || '/uai-icon.png';
                    const isComingSoon = isAppComingSoon(app);
                    return (
                      <div
                        key={app.id}
                        role="button"
                        tabIndex={0}
                        className={`column-box-item ${isComingSoon ? 'coming-soon' : ''}`}
                        onClick={() => !isComingSoon && handleAppClick(app.id)}
                        onKeyDown={(e) => e.key === 'Enter' && !isComingSoon && handleAppClick(app.id)}
                      >
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <img 
                            src={imageUrl} 
                            alt={app.title} 
                            className="column-box-icon" 
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/uai-icon.png';
                            }}
                          />
                          {isComingSoon && (
                            <div className="gplay-coming-soon-overlay">
                              <span>Coming Soon</span>
                            </div>
                          )}
                        </div>
                        <div className="column-box-info">
                          <h3 className="column-box-item-title">{app.title}</h3>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <button
                type="button"
                className="case-scroll-btn case-scroll-btn-right"
                onClick={() => handleProductivityScrollClick('right')}
                disabled={!canScrollProductivityRight}
                aria-label="Scroll right"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>



        </div>
      </main>

      {chatLayerMounted && (
        <main
          className={`app-store-main-whiteboard app-store-chat-fullview app-store-chat-layer${chatLayerActive ? ' is-active' : ''}`}
          aria-label="uAI Chat"
        >
          <div className="app-store-chat-fullview-inner">
            <ChatAgentPanel fullPage onOpenGuide={() => setChatOpen(true)} onClose={closeChatApp} />
          </div>
        </main>
      )}

      <AppBarBottom
        minimizedBubbles={allBubbles.filter((b) => !b.hidden && b.minimized)}
        onRestoreBubble={restoreBubble}
        onCloseBubble={deleteBubble}
        onToggleChat={toggleChatApp}
        isChatOpen={chatAppOpen}
        isChatFullView={chatAppOpen}
        autoHide={false}
        onHomeClick={chatAppOpen ? closeChatApp : undefined}
        apps={apps}
      />

      <ChatSidePanel
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        onNavigate={(path) => {
          navigate(path);
          setChatOpen(false);
        }}
        apps={apps}
      />
    </div>
  );
};

export default AppStoreView;
